import {
  line as lined3,
  curveMonotoneX,
  curveLinear,
  area as aread3
} from "d3";
import { dateInterpolate } from "@/_core/charts/D3ChartHoverHelpers";

export class Line {
  constructor(svg, svgAux = null) {
    this.svg = svg;
    this.svgAux = svgAux ? svgAux : svg;
  }

  // SINGLE LINE
  line({
    data,
    color,
    lineWidth = 2,
    fieldMame = "value",
    customX = false,
    deltaBand = 1 / 22,
    xRange = [],
    xIsDate = false,
    fieldLine = "line",
    date = "date"
  }) {
    // ======= Band Width =======
    let bandWidth = this.calculateBandWidth(xIsDate);
    const deltaCustom = bandWidth * deltaBand;

    // ======= Line =======
    let line = lined3()
      .curve(curveMonotoneX)
      .defined(d => d[fieldMame])
      .x(d =>
        customX
          ? dateInterpolate(xRange, d[date], this.svg.x, bandWidth) +
            deltaCustom -
            lineWidth / 2
          : this.svg.x(d[date]) + bandWidth / 2
      )
      .y(d => this.svg.y(d[fieldMame]));

    // ======= Data join =======
    this.dataJoin(fieldLine, lineWidth, data, color, line);

    return this.svg;
  }

  customLine({
    strokeColor,
    xMin,
    xMax,
    yMin,
    yMax,
    name,
    strokeWidth = 1,
    dashArray = null,
    isBandWidthMin = true,
    isBandWidthMax = true,
    reductionLeft = 0,
    reductionRight = 0,
    svgAux = null
  }) {
    const svgNodeAux = svgAux ? svgAux : this.svgAux;
    const bandWidthMin = this.calculateBandWidth(isBandWidthMin),
      bandWidthMax = this.calculateBandWidth(isBandWidthMax);

    if (!this.svg.lineGroup) {
      this.svg.lineGroup = svgNodeAux;
    }

    this.svg.lineGroup
      .append("line")
      .attr("class", `zoom-line line-${name}`)
      .style("opacity", 1)
      .attr("stroke", strokeColor)
      .attr("stroke-width", strokeWidth)
      .style("shape-rendering", "crispEdges")
      .style("stroke-dasharray", dashArray)
      .attr("x1", this.svg.x(xMin) + bandWidthMin + reductionLeft)
      .attr("x2", this.svg.x(xMax) + bandWidthMax + reductionRight)
      .attr("y1", this.svg.y(yMin))
      .attr("y2", this.svg.y(yMax));
  }

  // SINGLE LINE 2 AXIS
  line2Axis({
    data,
    color,
    lineWidth = 2,
    fieldMame = "value",
    xIsDate = false,
    fieldLine = "line"
  }) {
    // ======= Band With =======
    let bandWidth = this.calculateBandWidth(xIsDate);

    // ======= Line 2 Axis =======
    let line = lined3()
      .curve(curveMonotoneX)
      .defined(d => d[fieldMame])
      .x(d => this.svg.x(d.date) + bandWidth / 2)
      .y(d => this.svg.y2(d[fieldMame]));

    // ======= Data join =======
    this.dataJoin(fieldLine, lineWidth, data, color, line);
  }

  // MULTILINE
  multiLine({ data, colorSelection, lineWidth = 2, selectedVar = -1 }) {
    let bandWidth = this.svg.x.bandwidth();
    let line = lined3()
      .curve(curveLinear) // .curve(curveMonotoneX)
      .defined(d => d)
      .x((d, i) => this.svg.x(data.dates[i]) + bandWidth / 2)
      .y(d => this.svg.y(d));

    this.svg.path = this.svg
      .append("g")
      .attr("fill", "none")
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .selectAll("path")
      .data(data.series)
      .join("path")
      .style("stroke", (d, i) => colorSelection[i])
      .style("stroke-width", lineWidth)
      .attr("d", d => line(d.values))
      .classed("highlighted", function(d, i) {
        return i === selectedVar;
      })
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");

    return this.svg;
  }

  // LINES AREA
  linesArea({
    data,
    upperLineInfo = {
      color: null,
      width: 1,
      isDashed: false,
      upperName: "upper",
      field: "upperLine"
    },
    lowerLineInfo = {
      color: null,
      width: 1,
      isDashed: false,
      lowerName: "lower",
      field: "lowerLine"
    },
    areaColor,
    xIsDate = false,
    fieldArea = "area",
    drawArea = true,
    fieldXName = "date"
  }) {
    // ======= Band With =======
    let bandWidth = this.calculateBandWidth(xIsDate);

    // ======= Lines =======
    let upperLine = this.createLine(
      upperLineInfo.upperName,
      bandWidth,
      fieldXName
    );
    let lowerLine = this.createLine(
      lowerLineInfo.lowerName,
      bandWidth,
      fieldXName
    );

    // ======= Area =======
    if (drawArea) {
      let area = this.createArea(
        upperLineInfo.upperName,
        lowerLineInfo.lowerName,
        bandWidth
      );
      // ======= Area in between =======
      this.createAreaInBetween(fieldArea, data, area, areaColor);
    }

    // ======= Data join for paths =======
    this.multiLineDataJoin(upperLineInfo, data, upperLine);
    this.multiLineDataJoin(lowerLineInfo, data, lowerLine);
  }

  // MULTI LINES AREA
  multiLinesArea = function({
    svg,
    data,
    lineColor,
    lineWidth = 1,
    areaColor,
    nameList,
    linesDashed = false,
    xIsDate = false,
    fieldLine = "fieldLine",
    svgAux = null,
    drawArea = true
  }) {
    // ======= Band With =======
    let bandWidth = this.calculateBandWidth(xIsDate);

    let lineFct, areaFct, fieldName1, fieldName2;
    let dashArray = linesDashed === true ? "5, 5" : null;
    for (let i = 0; i < nameList.length - 1; i++) {
      fieldName1 = nameList[i];
      fieldName2 = nameList[i + 1];
      if (drawArea) {
        // ======= Area =======
        areaFct = this.createArea(fieldName1, fieldName2, bandWidth);

        // ======= Area between lines =======
        this.createAreaInBetween(
          fieldLine + "Area" + i,
          data,
          areaFct,
          areaColor[i]
        );
      }
      // ======= Line =======
      lineFct = this.createLine(fieldName1, bandWidth);

      svg[fieldLine + "Line" + i] = svgAux
        .append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", lineColor[i])
        .attr("stroke-width", lineWidth)
        .attr("stroke-linejoin", "round")
        .attr("stroke-linecap", "round")
        .attr("d", lineFct)
        .style("stroke-dasharray", dashArray)
        .style("shape-rendering", "crispEdges")
        .style("vector-effect", "non-scaling-stroke");
      if (i === nameList.length - 2) {
        lineFct = lined3()
          .defined(d => d[fieldName2])
          .x(d => svg.x(d.date) + bandWidth / 2)
          .y(d => svg.y(d[fieldName2]));
        svg[fieldLine + "Line" + (i + 1)] = svgAux
          .append("path")
          .datum(data)
          .attr("fill", "none")
          .attr("stroke", lineColor[i + 1])
          .attr("stroke-width", lineWidth)
          .attr("stroke-linejoin", "round")
          .attr("stroke-linecap", "round")
          .attr("d", lineFct)
          .style("stroke-dasharray", dashArray)
          .style("shape-rendering", "crispEdges")
          .style("vector-effect", "non-scaling-stroke");
      }
    }
  };

  // GENERAL LINE
  lineGeneral({
    strokeColor,
    x1 = 0,
    x2 = this.svg.width,
    y1 = 0,
    y2 = 0,
    strokeWidth = 1,
    strokeOpacity = 1,
    className = "xAxisLine",
    renderShape = "crispEdges"
  }) {
    this.svg
      .append("line")
      .attr("class", className)
      .attr("stroke-width", strokeWidth)
      .attr("stroke-opacity", strokeOpacity)
      .attr("stroke", strokeColor)
      .attr("x1", x1)
      .attr("y1", y1)
      .attr("x2", x2)
      .attr("y2", y2)
      .style("shape-rendering", renderShape);
  }

  // GENERAL METHODS
  dataJoin(fieldLine, lineWidth, data, color, line) {
    this.svg[fieldLine] = this.svgAux
      .append("g")
      .attr("fill", "none")
      .attr("stroke-width", lineWidth)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .style("shape-rendering", "crispEdges")
      .selectAll("path")
      .data([data])
      .join("path")
      .style("stroke", color)
      .attr("d", d => line(d))
      .style("vector-effect", "non-scaling-stroke");
  }

  multiLineDataJoin(info, data, line) {
    this.svg[info.field] = this.svgAux
      .append("path")
      .datum(data)
      .attr("fill", "none")
      .attr("stroke", info.color)
      .attr("stroke-width", info.width)
      .attr("stroke-linejoin", "round")
      .attr("stroke-linecap", "round")
      .attr("d", line)
      .style("stroke-dasharray", () => {
        return info.isDashed ? "5, 5" : "0";
      })
      .style("shape-rendering", "crispEdges")
      .style("vector-effect", "non-scaling-stroke");
  }

  createLine(fieldName, bandWidth, fieldXName = "date") {
    let line = lined3()
      .defined(d => d[fieldName])
      .x(d => this.svg.x(d[fieldXName]) + bandWidth / 2)
      .y(d => this.svg.y(d[fieldName]));
    return line;
  }

  createArea(fieldName1, fieldName2, bandWidth) {
    let area = aread3()
      .defined(d => d[fieldName1])
      .defined(d => d[fieldName2])
      .x(d => this.svg.x(d.date) + bandWidth / 2)
      .y0(d => this.svg.y(d[fieldName1]))
      .y1(d => this.svg.y(d[fieldName2]));
    return area;
  }

  createAreaInBetween(field, data, area, areaColor) {
    this.svg[field] = this.svgAux
      .append("path")
      .datum(data)
      .attr("fill", areaColor)
      .attr("d", area);
  }

  calculateBandWidth(xIsDate) {
    let bandWidth = 0;
    if (!xIsDate) bandWidth = this.svg.x.bandwidth();
    return bandWidth;
  }
}
